import React, { lazy } from 'react';
import { Route, useParams } from 'react-router';
import { DetailRoutes, PlayerRoutes, StaticPageRoutes } from 'types/RouteTypes';
import ApplicationConfig from 'providers/useConfig/ApplicationConfig';
import { MainMenuItem, NavigationAllLanguages } from '../types/Navigation';

const PageModular = lazy(() => import('components/Pages/PageModular/PageModular'));
const PageProgramDetail = lazy(() => import('../components/Pages/PageProgramDetail/PageProgramDetail'));
const PageMovieDetail = lazy(() => import('../components/Pages/PageMovieDetail/PageMovieDetail'));
const PageVODSeriesDetail = lazy(() => import('../components/Pages/PageVODSeriesDetail/PageVODSeriesDetail'));
const PageTVSeriesDetail = lazy(() => import('../components/Pages/PageTVSeriesDetail/PageTVSeriesDetail'));
const PageRecordingDetail = lazy(() => import('../components/Pages/PageRecordingDetail/PageRecordingDetail'));
const PageCollection = lazy(() => import('../components/Pages/PageCollection/PageCollection'));
const PageLanguage = lazy(() => import('../components/Pages/Settings/PageLanguage/PageLanguage'));
const MenuLanguage = lazy(() => import('../components/Pages/Settings/PageLanguage/MenuLanguage'));
const AudioLanguage = lazy(() => import('../components/Pages/Settings/PageLanguage/AudioLanguage'));
const SubtitlesLanguage = lazy(() => import('../components/Pages/Settings/PageLanguage/SubtitlesLanguage'));
const LivePlayer = lazy(() => import('../components/Pages/LivePlayer/LivePlayer'));
const CatchupPlayer = lazy(() => import('../components/Pages/CatchupPlayer/CatchupPlayer'));
const TrailerPlayer = lazy(() => import('../components/Pages/TrailerPlayer/TrailerPlayer'));
const VodPlayer = lazy(() => import('../components/Pages/VodPlayer/VodPlayer'));
const RecordingPlayer = lazy(() => import('../components/Pages/RecordingPlayer/RecordingPlayer'));

export const PATH_COLLECTION = 'collection';
export const PATH_EDITORIAL = 'editorial';

export const detailPageSectionRouting = [
    '/:section/:subSection/:sectionId/:foreignId',
    '/:section/:subSection/:sectionId',
    '/:section/:sectionId',
    '/:section',
    '',
];

export const collectionPageSectionRouting = ['/:section', ''];

export const DetailComponentMapping = ['en', 'et', 'ru']
    .map((language) => ({
        [language]: {
            [DetailRoutes[language].VOD_SERIES]: PageVODSeriesDetail,
            [DetailRoutes[language].PROGRAM]: PageProgramDetail,
            [DetailRoutes[language].MOVIE]: PageMovieDetail,
            [DetailRoutes[language].TV_SERIES]: PageTVSeriesDetail,
            [DetailRoutes[language].RECORDING]: PageRecordingDetail,
        },
    }))
    .reduce((acc, item) => ({ ...acc, ...item }));

export const PlayerComponentMapping = {
    [PlayerRoutes.LIVE]: LivePlayer,
    [PlayerRoutes.CATCHUP]: CatchupPlayer,
    [PlayerRoutes.TRAILER]: TrailerPlayer,
    [PlayerRoutes.VOD]: VodPlayer,
    [PlayerRoutes.RECORDING]: RecordingPlayer,
};

export const getMenuItemPath = (menuItem: MainMenuItem): string => `/${menuItem.title.replace(/\s+/g, '-').toLowerCase()}`;

export const isMenuItemActive = (pathname: string, menuPath: string, menuItemIndex: number) => {
    const isHomePage = pathname === '/' && menuItemIndex === 0;
    return pathname.indexOf(menuPath.slice(1)) > 0 || isHomePage;
};

export const buildNavigationRoutes = (navigationData: NavigationAllLanguages) => {
    const routes = [];

    if (!navigationData) {
        return routes;
    }

    Object.keys(navigationData).forEach((language) => {
        navigationData[language]?.menuItems?.forEach((item) => {
            routes.push(
                <Route
                    key={`${language}-${item.id}`}
                    path={getMenuItemPath(item)}
                    element={<PageModular pageId={item.pageId} itemId={item.id} />}
                />
            );
        });
    });

    const homePageId = Object.values(navigationData).find((nav) => nav?.homePageId)?.homePageId;
    if (homePageId) {
        routes.push(<Route key="home-root" path="/" element={<PageModular pageId={homePageId} />} />);
    }

    const searchPageId = ApplicationConfig.api_config?.routes?.Zattoo?.search_page_id;
    if (searchPageId) {
        routes.push(<Route key="search" path={StaticPageRoutes.SEARCH} element={<PageModular pageId={searchPageId} />} />);
    }

    return routes;
};

export const buildSectionDetailRouting = (appLanguage: string) => {
    const routes = [];

    detailPageSectionRouting.forEach((sectionConfig) => {
        Object.keys(DetailComponentMapping[appLanguage]).forEach((key) => {
            const Component = DetailComponentMapping[appLanguage][key];
            routes.push(<Route key={`${sectionConfig}${key}`} path={`${sectionConfig}${key}`} element={<Component pageId={key} />} />);
        });
    });

    return routes;
};

export const buildSectionCollectionRouting = () => {
    const routes = [];

    collectionPageSectionRouting.forEach((sectionConfig) => {
        const path = `${sectionConfig}${StaticPageRoutes.COLLECTION}`;
        routes.push(<Route key={path} path={path} element={<PageCollection />} />);
    });

    return routes;
};

const ExpandablePageFromUrl: React.FC = () => {
    const { pageId } = useParams<{ pageSlug?: string; pageId?: string }>();
    return <PageModular pageId={pageId} itemId={undefined} />;
};

export const buildExpandablePageRouting = () => [
    <Route key={StaticPageRoutes.EXPANDABLE_PAGE} path={StaticPageRoutes.EXPANDABLE_PAGE} element={<ExpandablePageFromUrl />} />,
];

export const buildPlayerRoutes = (enabled: boolean) => {
    if (!enabled) return [];
    return Object.keys(PlayerComponentMapping).map((path) => <Route key={path} path={path} element={null} />);
};

export const buildLanguageSettingsRoutes = () => {
    const routes = [];

    routes.push(<Route key={StaticPageRoutes.LANGUAGE} path={StaticPageRoutes.LANGUAGE} element={<PageLanguage />} />);
    routes.push(<Route key={StaticPageRoutes.MENU_LANGUAGE} path={StaticPageRoutes.MENU_LANGUAGE} element={<MenuLanguage />} />);
    routes.push(<Route key={StaticPageRoutes.AUDIO_LANGUAGE} path={StaticPageRoutes.AUDIO_LANGUAGE} element={<AudioLanguage />} />);
    routes.push(
        <Route key={StaticPageRoutes.SUBTITLES_LANGUAGE} path={StaticPageRoutes.SUBTITLES_LANGUAGE} element={<SubtitlesLanguage />} />
    );

    return routes;
};
